import { useConfig } from "@/context/ConfigContext";
import { useEffect, useState } from "react";

/**
 * Hook untuk mengambil produk dari API
 * @param {Object} options - Options untuk fetch
 * @param {number|string} options.id - ID produk spesifik (optional)
 * @param {string} options.slug - Slug produk spesifik (optional)
 * @param {Object} options.params - Query parameters (optional)
 * @param {string} options.params.category - Filter by category slug (prioritas di atas category_id)
 * @param {number} options.params.category_id - Filter by category ID
 * @param {number} options.params.collection_id - Filter by collection ID
 * @param {string} options.params.search - Search keyword
 * @param {number} options.params.page - Page number
 * @param {number} options.params.per_page - Items per page
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useProducts(options = {}) {
  const { id, slug, params = {}, enabled = true } = options;
  const { config, configData } = useConfig();
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    // Pastikan config sudah ready dan team_id tersedia
    if (!enabled || !configData?.team_id || !config?.api?.baseUrl) {
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const teamId = configData.team_id;

        // Build query string dari params
        const queryParams = new URLSearchParams();
        if (params.category) queryParams.append("category", params.category);
        else if (params.category_id)
          queryParams.append("category_id", params.category_id);
        if (params.collection_id)
          queryParams.append("collection_id", params.collection_id);
        if (params.search) queryParams.append("search", params.search);
        if (params.page) queryParams.append("page", params.page);
        if (params.per_page) queryParams.append("per_page", params.per_page);

        // Forward filter tambahan lain yang didukung backend (sort, min/max price, dll)
        // Hindari duplikasi dengan key yang sudah di-append di atas.
        const forwardedKeys = new Set([
          "category",
          "category_id",
          "collection_id",
          "search",
          "page",
          "per_page",
        ]);
        Object.entries(params).forEach(([key, value]) => {
          if (forwardedKeys.has(key)) return;
          if (value === undefined || value === null || value === "") return;
          queryParams.append(key, value);
        });

        const queryString = queryParams.toString();

        // Gunakan proxy API route untuk menghindari CORS
        // API route akan otomatis mengambil baseUrl dari config berdasarkan domain
        const productKey = slug || id;
        const proxyUrl = productKey
          ? `/api/products?team_id=${teamId}&type=products&slug=${productKey}${
              queryString ? `&${queryString}` : ""
            }`
          : `/api/products?team_id=${teamId}&type=products${
              queryString ? `&${queryString}` : ""
            }`;

        // Log request details untuk debugging
        // console.log("[useProducts] Fetching products:", {
        //   proxyUrl,
        //   teamId,
        //   id,
        //   slug,
        //   params,
        //   baseUrl: config?.api?.baseUrl,
        // });

        const response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            // Jika bukan JSON, gunakan text sebagai error message
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          // Ambil apiUrl dari errorData jika ada (dikirim dari API route)
          const apiUrl = errorData.apiUrl || "N/A";

          // Build detailed error message
          const errorDetails = {
            proxyEndpoint: proxyUrl,
            laravelApiUrl: apiUrl, // URL lengkap Laravel API yang di-hit
            status: response.status,
            statusText: response.statusText,
            teamId: teamId,
            params: params,
            apiBaseUrl: config?.api?.baseUrl,
            errorMessage:
              errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText,
            errorDetail: errorData.errorDetail || errorData.errorData,
            errorData: errorData,
          };

          console.error("[useProducts] API Error:", errorDetails);

          throw new Error(
            `API Error\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Proxy Endpoint: ${proxyUrl}\n` +
              `Laravel API URL: ${apiUrl}\n` +
              `Status: ${response.status} ${response.statusText}\n` +
              `Team ID: ${teamId}\n` +
              `Params: ${JSON.stringify(params)}\n` +
              `API Base URL: ${config?.api?.baseUrl}\n` +
              `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
              `Error Message: ${
                errorData.message ||
                errorData.error ||
                errorText ||
                response.statusText
              }\n` +
              `Error Detail: ${
                errorData.errorDetail
                  ? errorData.errorDetail
                  : JSON.stringify(errorData, null, 2)
              }`,
          );
        }

        const data = await response.json();

        // Handle response berdasarkan apakah ada id atau tidak
        if (productKey) {
          // Single product
          setProduct(data.data || data);
        } else {
          // List products (normalize agar selalu array)
          // Handle pagination jika ada
          if (data.meta || data.pagination || data.data?.meta) {
            setPagination(data.meta || data.pagination || data.data?.meta);
          }

          const rawData = data.data !== undefined ? data.data : data;
          let normalized = [];

          if (Array.isArray(rawData)) {
            normalized = rawData;
          } else if (rawData && typeof rawData === "object") {
            // Prioritas field umum
            if (Array.isArray(rawData.products)) {
              normalized = rawData.products;
            } else if (Array.isArray(rawData.product)) {
              // product bisa berupa array
              normalized = rawData.product;
            } else if (Array.isArray(rawData.data)) {
              normalized = rawData.data;
            } else if (Array.isArray(rawData?.data?.products)) {
              normalized = rawData.data.products;
            } else if (Array.isArray(rawData?.data?.product)) {
              normalized = rawData.data.product;
            } else if (rawData?.data?.product) {
              normalized = [rawData.data.product];
            } else if (rawData.product) {
              normalized = [rawData.product];
            } else {
              // Jika response adalah single product/collection dengan product_id saja, fallback ke object-nya
              normalized = [rawData];
            }
          }

          setProducts(normalized);
        }
      } catch (err) {
        console.error("[useProducts] Error fetching products:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          params: params,
          baseUrl: config?.api?.baseUrl,
        });
        setError(err.message || "Gagal mengambil produk");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    id,
    slug,
    enabled,
    JSON.stringify(params), // Re-fetch jika params berubah
  ]);

  const hasSingleProduct = !!(slug || id);

  return {
    products: hasSingleProduct ? null : products,
    product: hasSingleProduct ? product : null,
    loading,
    error,
    pagination,
  };
}
